<template>
  <v-dialog
    v-if="dialogOpen"
    :value="dialog"
    persistent
    :retain-focus="false"
    transition="fade-transition"
    fullscreen
    @keydown.esc="escape(closeDialog)"
  >
    <v-toolbar
      color="primaryBgr"
      dark
    >
      <p-btn-icon type="close" btn-class="mr-4" tabindex="3" @click="escape(closeDialog)"/>
      <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn text tabindex="2" :disabled="!isModelChanged()" @click="submit">{{ $t('save') }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <v-card>
      <v-form ref="form" @submit.stop.prevent>
        <div class="p-dialog-full-screen-container">
          <v-row class="mt-0">
            <v-col cols="12" md="4" sm="6">
              <v-text-field
                type="text"
                v-model.trim="editedTemplate.name"
                :label="$t('contractTemplate.name')"
                :rules="rules.name"
                class="p-input-lg"
                tabindex="1"
                clearable
                autofocus
              ></v-text-field>
              <v-checkbox
                v-model="editedTemplate.promoter"
                :label="$t('contractTemplate.promoter')"
                tabindex="1"
                hide-details
              ></v-checkbox>
            </v-col>
              </v-row>
          <v-row>
            <v-col cols="12" md="6" sm="6">
              <v-autocomplete
                ref="contractReceivers"
                v-model="editedTemplate.contractReceivers"
                :items="shortContactsWithEmail"
                :label="$t('contractTemplate.contractReceivers')"
                :return-object="false"
                item-value="id"
                item-text="displayNameWithCompany"
                prepend-icon="mdi-calendar-account-outline"
                tabindex="1"
                clearable
                multiple
                small-chips
                deletable-chips
                hide-selected
                @change="$refs.contractReceivers.lazySearch=''"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <ejs-documenteditorcontainer
                v-if="ejsReady"
                ref="editor"
                :serviceUrl="editorServiceUrl"
                :documentName="editedTemplate.name"
                :enableToolbar="true"
                :toolbarItems="editorToolbarItems"
                height="600px"
                tabindex="1"
                @created="onEditorCreated"
                @toolbarClick="onEditorToolbarClick"
                @contentChange="onEditorContentChange"
              ></ejs-documenteditorcontainer>
            </v-col>
          </v-row>
        </div>
      </v-form>
    </v-card>

    <placeholder-dialog
      :dialog.sync="globalPlaceholderDialog"
      :keys="placeholdersContractGlobal"
      :title="$t('documentEditor.insertGlobalPlaceholder')"
      @insert="insertPlaceholder"
    ></placeholder-dialog>

    <placeholder-dialog
      v-if="!editedTemplate.promoter"
      :dialog.sync="contactPlaceholderDialog"
      :keys="placeholdersContractContact"
      :title="$t('documentEditor.insertContactPlaceholder')"
      require-name
      @insert="insertPlaceholder"
    ></placeholder-dialog>

    <placeholder-dialog
      v-if="editedTemplate.promoter"
      :dialog.sync="eventPlaceholderDialog"
      :keys="placeholdersContractVenue"
      :title="$t('documentEditor.insertEventPlaceholder')"
      @insert="insertPlaceholder"
    ></placeholder-dialog>
  </v-dialog>
</template>


<script>
import Vue from 'vue'

import { mapActions, mapGetters } from 'vuex'

import PBtnIcon from '@/components/PBtnIcon'

import PlaceholderDialog from './ContractTemplatePlaceholderDialog'

import documentEditorMixin from '@/mixins/documentEditor.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'

import { DocumentEditorContainerPlugin, Toolbar } from '@syncfusion/ej2-vue-documenteditor'

Vue.use(DocumentEditorContainerPlugin)


export default {
  provide: {
    DocumentEditorContainer: [Toolbar]
  },


  components: {
    PlaceholderDialog,
    PBtnIcon
  },


  mixins: [
    documentEditorMixin,
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  props: {
    template: Object
  },


  data () {
    return {
      globalPlaceholderDialog: false,
      contactPlaceholderDialog: false,
      eventPlaceholderDialog: false,
      editorServiceUrl: process.env.VUE_APP_DOCUMENT_EDITOR_API_URL,
      editorCreated: false,
      editedTemplate: {},
      defaultTemplate: {
        name: '',
        template: '',
        promoter: false,
        contractReceivers: []
      },
      dialogOpen: false,
      ejsReady: false
    }
  },


  computed: {
    dialogTitle () {
      return this.template ? this.$t('contractTemplate.editTemplate') : this.$t('contractTemplate.createTemplate')
    },

    editorToolbarItems () {
      const docxItem = this.getToolbarItem(this.toolbarItemId.docx)
      const pdfItem = this.getToolbarItem(this.toolbarItemId.pdf)
      const globalPlaceholderItem = this.getToolbarItem(this.toolbarItemId.globalPlaceholder)
      const contactPlaceholderItem = this.getToolbarItem(this.toolbarItemId.contactPlaceholder)
      const eventPlaceholderItem = this.getToolbarItem(this.toolbarItemId.eventPlaceholder)

      return ['New', 'Open',
        'Separator', docxItem, pdfItem,
        'Separator', 'Undo', 'Redo',
        'Separator', globalPlaceholderItem, this.editedTemplate.promoter ? eventPlaceholderItem : contactPlaceholderItem, 'Image', 'Table', 'Hyperlink', 'TableOfContents',
        'Separator', 'Header', 'Footer', 'PageSetup', 'PageNumber', 'Break',
        'Separator', 'Find']
    },

    ...mapGetters('placeholder', ['placeholdersContractGlobal', 'placeholdersContractContact', 'placeholdersContractVenue']),
    ...mapGetters('contact', ['shortContactsWithEmail']),

    rulesToApply () {
      const rule = this.rule

      return {
        name: [
          rule.required()
        ]
      }
    }
  },


  methods: {
    ...mapActions('contractTemplate', ['createContractTemplate', 'updateContractTemplate']),
    ...mapActions('changes', ['removeFromChanges']),

    async onDialogOpen () {
      if (this.template) {
        this.removeFromChanges(this.template)
      }
      this.editedTemplate = this.getObjectCopy(this.template || this.defaultTemplate)
      this.prepareEditor()
    },

    async prepareEditor () {
      this.ejsReady = false
      await Vue.nextTick()
      this.dialogOpen = true
      await Vue.nextTick()
      this.ejsReady = true
      await Vue.nextTick()
      this.resetEditorSize()
      if (this.editorCreated) {
        this.setEditorContent()
      }
    },

    removeEditor () {
      this.ejsReady = false
    },

    onEditorCreated () {
      this.editorCreated = true
    },

    onEditorContentChange () {
      this.updateEditedTemplate()
    },

    async onEditorToolbarClick (args) {
      try {
        switch (args.item.id) {
          case this.toolbarItemId.globalPlaceholder:
            this.globalPlaceholderDialog = true
            break
          case this.toolbarItemId.contactPlaceholder:
            this.contactPlaceholderDialog = true
            break
          case this.toolbarItemId.eventPlaceholder:
            this.eventPlaceholderDialog = true
            break
          case this.toolbarItemId.pdf: {
            this.saveAsPdf(this.$refs.editor, this.editedTemplate.name || 'template')
            break
          }
          case this.toolbarItemId.docx: {
            this.saveAsDocx(this.$refs.editor, this.editedTemplate.name || 'template')
            break
          }
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    setEditorContent () {
      if (!this.$refs?.editor) return
      if (this.template) {
        this.$refs.editor.ej2Instances.documentEditor.open(this.template.template)
      } else {
        this.$refs.editor.ej2Instances.documentEditor.openBlank()
      }

      this.updateEditedTemplate()
      this.initWatchedModel('editedTemplate')
    },

    updateEditedTemplate () {
      this.editedTemplate.template = this.$refs.editor.ej2Instances.documentEditor.serialize()
    },

    insertPlaceholder (placeholder) {
      this.$refs.editor.ej2Instances.documentEditor.editor.insertText(placeholder)
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()

        if (this.editedTemplate.id) {
          await this.updateContractTemplate(this.editedTemplate)
          this.showSnackbarSuccess(this.$t('contractTemplate.templateUpdated'))
        } else {
          await this.createContractTemplate(this.editedTemplate)
          this.showSnackbarSuccess(this.$t('contractTemplate.templateCreated'))
        }

        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.removeEditor()
      }
    }
  }
}
</script>
